// src/pages/Legal/TermsOfServicePage.tsx
import React, { useState } from 'react';
import MainLayout from '../../components/MainLayout/MainLayout';
import { 
  FileText, 
  Scale, 
  Users, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  XCircle,
  Gavel,
  Globe,
  Calendar,
  ChevronRight,
  BookOpen,
  CreditCard,
  Ban,
  MessageCircle,
  Info,
  Star
} from 'lucide-react';

/**
 * PLATFORM STATUS: Company-Owned Content Platform
 * 
 * StrymHub is operated as a company platform where all content is created and produced 
 * by StrymHub and its founder. This is not a public creator platform.
 * 
 * FUTURE POSSIBILITY:
 * StrymHub may invite select creators for partnerships on an invitation-only basis.
 * This is not guaranteed and remains at the sole discretion of StrymHub.
 * 
 * AFFILIATED CONTENT:
 * The platform may feature content that promotes StrymHub's affiliated businesses and platforms.
 * 
 * Version: 1.0.0 - Company Platform Model
 */

const TermsOfServicePage = () => {
  const [activeSection, setActiveSection] = useState<string>('');

  const tableOfContents = [
    { id: 'agreement', title: 'Agreement to Terms' },
    { id: 'platform-status', title: 'Platform Status' },
    { id: 'service-description', title: 'Service Description' },
    { id: 'user-accounts', title: 'User Accounts' },
    { id: 'acceptable-use', title: 'Acceptable Use Policy' },
    { id: 'content-policy', title: 'Content Policy' },
    { id: 'intellectual-property', title: 'Intellectual Property' },
    // INVITE-ONLY: May be activated for select creator partnerships
    // { id: 'creator-terms', title: 'Creator Terms' },
    // { id: 'payment-terms', title: 'Payment Terms' },
    { id: 'privacy', title: 'Privacy and Data Protection' },
    { id: 'termination', title: 'Account Termination' },
    { id: 'disclaimers', title: 'Disclaimers and Warranties' },
    { id: 'liability', title: 'Limitation of Liability' },
    { id: 'governing-law', title: 'Governing Law' },
    { id: 'future-changes', title: 'Future Platform Changes' },
    { id: 'contact', title: 'Contact Information' }
  ];

  const prohibitedActivities = [
    {
      icon: Ban,
      title: 'Illegal Content',
      description: 'Uploading, sharing, or promoting illegal content or activities'
    },
    {
      icon: XCircle,
      title: 'Harassment',
      description: 'Bullying, harassment, or threatening other users'
    },
    {
      icon: AlertTriangle,
      title: 'Spam',
      description: 'Sending unsolicited messages or promotional content'
    },
    {
      icon: Shield,
      title: 'Impersonation',
      description: 'Impersonating other individuals or organizations'
    }
  ];

  const userRights = [
    {
      icon: CheckCircle,
      title: 'Content Access',
      description: 'Access to all platform content based on your subscription level'
    },
    {
      icon: BookOpen,
      title: 'Personal Use',
      description: 'Right to view and download content for personal, non-commercial use'
    },
    {
      icon: MessageCircle,
      title: 'Community Participation',
      description: 'Ability to comment, like, and engage with content and community'
    },
    {
      icon: Users,
      title: 'Account Management',
      description: 'Full control over your account settings and privacy preferences'
    }
  ];

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-950 via-neutral-900 to-slate-950 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                  <Scale className="h-8 w-8 text-white" />
                </div>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                Terms of <span className="text-transparent font-display bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text">Service</span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                These terms govern your use of the StrymHub platform. By using our services, 
                you agree to these terms and conditions. StrymHub is currently operated as a 
                curated content platform by a single administrator.
              </p>
              
              <div className="flex items-center justify-center space-x-4 text-sm text-gray-300">
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  <span>Last updated: December 2025</span>
                </div>
                <div className="flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  <span>Version: 1.0.0 (Single Creator)</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="lg:grid lg:grid-cols-4 lg:gap-8">
            {/* Table of Contents */}
            <div className="lg:col-span-1">
              <div className="sticky top-8">
                <div className="bg-white rounded-2xl p-6 shadow-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h3>
                  <nav className="space-y-1">
                    {tableOfContents.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors duration-200 flex items-center ${
                          activeSection === item.id
                            ? 'bg-orange-100 text-orange-700 font-medium'
                            : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                        }`}
                      >
                        <ChevronRight className="h-4 w-4 mr-2" />
                        {item.title}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3 mt-8 lg:mt-0">
              
              {/* Agreement to Terms */}
              <section id="agreement" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Gavel className="h-6 w-6 text-orange-500 mr-3" />
                    Agreement to Terms
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      By accessing and using StrymHub ("the Service"), you accept and agree to be bound by the 
                      terms and provision of this agreement. If you do not agree to abide by the above, please 
                      do not use this service.
                    </p>
                    
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">Important Notice</h4>
                          <p className="text-sm">
                            These terms may be updated from time to time, particularly as we expand the platform 
                            to include more content creators. Continued use of the service after changes constitutes 
                            acceptance of the new terms.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <p>
                      <strong>Who Can Use Our Service:</strong> You must be at least 13 years old to create an 
                      account. Users between 13-18 must have parental consent. By using our service, you 
                      represent that you meet these age requirements.
                    </p>
                  </div>
                </div>
              </section>

              {/* Platform Status - Current Single Creator Model */}
              <section id="platform-status" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-orange-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="h-6 w-6 text-orange-500 mr-3" />
                    Current Platform Status
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                        <div className="text-orange-800">
                          <h4 className="font-semibold mb-1">Company Platform</h4>
                          <p className="text-sm">
                            StrymHub is a company-owned content platform. All content is created and produced 
                            by StrymHub and its founder. This ensures quality control and consistent content 
                            standards. The platform may also feature content promoting StrymHub's affiliated 
                            businesses and platforms.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                      <div className="border border-green-200 rounded-lg p-4 bg-green-50">
                        <h4 className="font-semibold text-green-900 mb-2">Platform Features</h4>
                        <ul className="text-sm text-green-800 space-y-1">
                          <li>• 100% Original content library</li>
                          <li>• Viewer accounts and profiles</li>
                          <li>• Personal libraries and favorites</li>
                          <li>• Community interaction (comments, likes)</li>
                          <li>• Quality-controlled, curated content</li>
                          <li>• Cross-promotion of affiliated platforms</li>
                        </ul>
                      </div>
                      
                      <div className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                        <h4 className="font-semibold text-blue-900 mb-2">Future Possibilities</h4>
                        <ul className="text-sm text-blue-800 space-y-1">
                          <li>• Invite-only creator partnerships</li>
                          <li>• Collaboration with select creators</li>
                          <li>• Premium content tiers</li>
                          <li>• Enhanced viewing features</li>
                          <li>• Expanded content library</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg mt-4">
                      <h4 className="font-semibold text-gray-900 mb-2">Creator Partnerships</h4>
                      <p className="text-sm text-gray-700">
                        StrymHub is not a public creator platform. However, we may invite select creators 
                        for partnerships on an invitation-only basis. If you're interested in a potential 
                        partnership, you can contact us, but please note that partnerships are offered 
                        at StrymHub's sole discretion and are not guaranteed.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Service Description */}
              <section id="service-description" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Service Description</h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      StrymHub is a company-owned content streaming platform that provides access to original content 
                      including originals, cinema, podcasts, animations, and other multimedia content. All content 
                      is created by StrymHub and its founder — 100% original, conceived, filmed, and edited in-house. 
                      The platform may also feature content that promotes StrymHub's affiliated businesses and platforms. 
                      Our service includes:
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Content Library</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Interactive originals and articles</li>
                          <li>• Premium cinema and video content</li>
                          <li>• Podcast episodes and series</li>
                          <li>• Animated content and shorts</li>
                          <li>• Exclusive sneak peeks</li>
                        </ul>
                      </div>
                      
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-2">Platform Features</h4>
                        <ul className="text-sm space-y-1">
                          <li>• Personalized content recommendations</li>
                          <li>• Community interaction and comments</li>
                          <li>• Personal library and favorites</li>
                          <li>• Multi-device synchronization</li>
                          <li>• Offline content access</li>
                        </ul>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <strong>Note:</strong> StrymHub is a company-owned platform where all content is 
                        created by the founder. Content upload functionality is restricted to authorized 
                        administrators. Viewer accounts can enjoy all consumption features including watching, 
                        commenting, creating playlists, and building personal libraries.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* User Accounts */}
              <section id="user-accounts" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="h-6 w-6 text-orange-500 mr-3" />
                    User Accounts
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <h4 className="font-semibold text-gray-900">Account Registration</h4>
                    <p>
                      To access certain features of our service, you must register for an account. You agree to:
                    </p>
                    
                    <ul className="space-y-2 ml-6">
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Provide accurate and complete information during registration</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Keep your login credentials secure and confidential</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Update your information to keep it current</span>
                      </li>
                      <li className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5" />
                        <span>Notify us immediately of any unauthorized account access</span>
                      </li>
                    </ul>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-900 mb-2">Account Security</h4>
                      <p className="text-red-800 text-sm">
                        You are responsible for all activities that occur under your account. Do not share 
                        your account credentials with others.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Acceptable Use Policy */}
              <section id="acceptable-use" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Acceptable Use Policy</h2>
                  
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-4">Your Rights as a User</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {userRights.map((right, index) => (
                        <div key={index} className="flex items-start p-4 border border-green-200 rounded-lg bg-green-50">
                          <right.icon className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                          <div>
                            <h5 className="font-semibold text-green-900">{right.title}</h5>
                            <p className="text-sm text-green-800">{right.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Prohibited Activities</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {prohibitedActivities.map((activity, index) => (
                        <div key={index} className="flex items-start p-4 border border-red-200 rounded-lg bg-red-50">
                          <activity.icon className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                          <div>
                            <h5 className="font-semibold text-red-900">{activity.title}</h5>
                            <p className="text-sm text-red-800">{activity.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>

              {/* Payment Terms - COMING SOON */}
              {/* 
                FUTURE - Payment & Subscription Features: Coming Soon
                Payment functionality is not yet implemented. When launched, will include:
                - Viewer subscription plans with different access levels
                - Partner revenue sharing if partnerships established (70% ad revenue, 85% tips, 80% subscriptions)
                - Minimum payout thresholds ($50 USD)
                - Payment methods (PayPal, Stripe, bank transfer)
                - Refund policies and billing management
              */}
              <section id="payment-terms" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <CreditCard className="h-6 w-6 text-orange-500 mr-3" />
                    Payment Terms
                    <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">Coming Soon</span>
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">Payment Features Coming Soon</h4>
                          <p className="text-sm">
                            StrymHub is currently free to use! Payment and subscription features are not yet 
                            implemented. We're focused on building a quality content library first. When payment 
                            features launch, this section will be updated with full terms and conditions.
                          </p>
                        </div>
                      </div>
                    </div>

                    <h4 className="font-semibold text-gray-900">Current Access</h4>
                    <p>
                      All content on StrymHub is currently available for free to registered users. 
                      No payment is required to access any content during this phase.
                    </p>

                    <h4 className="font-semibold text-gray-900">Future Plans</h4>
                    <p>
                      When we implement payment features, we plan to offer:
                    </p>
                    
                    <ul className="space-y-2 ml-6">
                      <li>• Various subscription tiers with different access levels</li>
                      <li>• Secure payment processing through trusted providers</li>
                      <li>• Flexible billing options (monthly, annual)</li>
                      <li>• Fair refund policies for new subscribers</li>
                    </ul>

                    {/* 
                      INVITE-ONLY: Partner payment terms - Uncomment if partnerships are established
                      
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Partner Payment Terms</h4>
                      <p className="mb-4">
                        Partners can earn money through multiple channels on StrymHub:
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">Revenue Streams</h5>
                          <ul className="text-sm space-y-1">
                            <li>• Ad Revenue: 70% of advertising revenue</li>
                            <li>• Tips & Donations: 85% of viewer tips</li>
                            <li>• Subscriptions: 80% of subscription revenue</li>
                            <li>• Pay-Per-View: 80% of PPV purchases</li>
                          </ul>
                        </div>
                        
                        <div className="border border-gray-200 rounded-lg p-4">
                          <h5 className="font-semibold text-gray-900 mb-2">Payment Details</h5>
                          <ul className="text-sm space-y-1">
                            <li>• Minimum payout: $50 USD</li>
                            <li>• Monthly payments (around 15th)</li>
                            <li>• PayPal, Stripe, or bank transfer</li>
                            <li>• Real-time earnings dashboard</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                    */}
                  </div>
                </div>
              </section>

              {/* Intellectual Property */}
              <section id="intellectual-property" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Intellectual Property Rights</h2>
                  <div className="text-gray-700 space-y-4">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                      <div className="flex items-start">
                        <Info className="h-5 w-5 text-orange-600 mr-3 mt-0.5" />
                        <div className="text-orange-800">
                          <h4 className="font-semibold mb-1">100% Original Content</h4>
                          <p className="text-sm">
                            All content on StrymHub is 100% original. Every video, animation, and podcast is 
                            conceived, filmed, and edited by the platform founder. Nothing is copied from other 
                            platforms or creators. All intellectual property rights belong to StrymHub. If we 
                            establish invite-only creator partnerships in the future, partners would retain 
                            ownership of their content while granting us license rights.
                          </p>
                        </div>
                      </div>
                    </div>

                    <p>
                      All content on StrymHub, including but not limited to text, graphics, logos, images, 
                      videos, audio clips, and software, is the property of StrymHub and is protected by 
                      copyright, trademark, and other intellectual property laws.
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="border-l-4 border-green-500 pl-4">
                        <h4 className="font-semibold text-green-900">What You Can Do</h4>
                        <ul className="text-sm text-green-800 mt-2 space-y-1">
                          <li>• View and stream content for personal use</li>
                          <li>• Share links to content on social media</li>
                          <li>• Download content for offline viewing (where available)</li>
                          <li>• Comment and engage with the community</li>
                        </ul>
                      </div>
                      
                      <div className="border-l-4 border-red-500 pl-4">
                        <h4 className="font-semibold text-red-900">What You Cannot Do</h4>
                        <ul className="text-sm text-red-800 mt-2 space-y-1">
                          <li>• Copy, distribute, or modify content</li>
                          <li>• Use content for commercial purposes</li>
                          <li>• Remove copyright or proprietary notices</li>
                          <li>• Reverse engineer or extract content</li>
                        </ul>
                      </div>
                    </div>

                    {/* 
                      INVITE-ONLY: Partner content rights - Uncomment if partnerships are established
                      
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <h4 className="font-semibold text-gray-900 mb-4">Partner Content Rights</h4>
                      <p className="mb-4">
                        When you upload content to StrymHub as a partner:
                      </p>
                      <ul className="space-y-2 ml-6">
                        <li>• You retain full ownership of your original content</li>
                        <li>• You grant StrymHub a license to display and distribute your content</li>
                        <li>• You represent that you have rights to all content you upload</li>
                        <li>• You are responsible for any copyright claims on your content</li>
                      </ul>
                    </div>
                    */}
                  </div>
                </div>
              </section>

              {/* Limitation of Liability */}
              <section id="liability" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Limitation of Liability</h2>
                  <div className="text-gray-700 space-y-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-yellow-600 mr-3 mt-0.5" />
                        <div className="text-yellow-800">
                          <h4 className="font-semibold mb-1">Important Legal Notice</h4>
                          <p className="text-sm">
                            StrymHub provides the service "as is" and disclaims all warranties, express or implied. 
                            We are not liable for any indirect, incidental, or consequential damages.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <p>
                      To the maximum extent permitted by law, StrymHub shall not be liable for any damages 
                      arising from your use of the service, including but not limited to:
                    </p>
                    
                    <ul className="space-y-1 ml-6">
                      <li>• Service interruptions or technical issues</li>
                      <li>• Loss of data or content</li>
                      <li>• Security breaches or unauthorized access</li>
                      <li>• Third-party content or services</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Governing Law */}
              <section id="governing-law" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Globe className="h-6 w-6 text-orange-500 mr-3" />
                    Governing Law
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      These Terms of Service shall be governed by and construed in accordance with the laws 
                      of England and Wales, without regard to its conflict of law provisions.
                    </p>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Dispute Resolution</h4>
                      <p className="text-sm">
                        Any disputes arising from these terms or your use of the service will be resolved 
                        through binding arbitration in London, United Kingdom, except where prohibited by law.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Contact Information */}
              <section id="contact" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      If you have any questions about these Terms of Service, please contact us:
                    </p>
                    
                    <div className="bg-gray-50 p-6 rounded-lg">
                      <div className="space-y-3">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>Legal Department:</strong> legal@strymhub.com</span>
                        </div>
                        <div className="flex items-center">
                          <MessageCircle className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>General Support:</strong> info.strymhub@gmail.com</span>
                        </div>
                        <div className="flex items-center">
                          <Globe className="h-5 w-5 text-orange-500 mr-3" />
                          <span><strong>Address:</strong> London, United Kingdom</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                      <h4 className="font-semibold text-blue-900 mb-2">Interested in a Partnership?</h4>
                      <p className="text-blue-800 text-sm">
                        StrymHub is a company platform, not a public creator platform. However, we may consider 
                        invite-only partnerships with select creators. If you believe you'd be a great fit, 
                        you can reach out to <strong>partnerships@strymhub.com</strong>. Please note that 
                        partnerships are offered at our sole discretion and are not guaranteed.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Future Possibilities */}
              <section id="future-changes" className="mb-12">
                <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-blue-200">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <Star className="h-6 w-6 text-blue-500 mr-3" />
                    Future Possibilities
                  </h2>
                  <div className="text-gray-700 space-y-4">
                    <p>
                      StrymHub is committed to growing and evolving to better serve our community. Here's what 
                      may be possible as our platform develops:
                    </p>

                    <div className="bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Invite-Only Creator Partnerships</h4>
                      <p className="text-sm text-gray-700 mb-4">
                        While StrymHub is a company-owned platform, we may invite select creators for partnerships 
                        in the future. These would be invitation-only collaborations, not open public applications.
                      </p>
                      
                      <h5 className="font-semibold text-gray-800 mb-2">What This Would Mean:</h5>
                      <ul className="text-sm text-gray-700 space-y-2">
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span><strong>For Viewers:</strong> Access to additional curated content from vetted partner creators</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span><strong>For Partners:</strong> Invitation to collaborate with StrymHub on content creation and distribution</span>
                        </li>
                        <li className="flex items-start">
                          <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span><strong>Quality Assurance:</strong> All partner content would meet StrymHub's quality and content standards</span>
                        </li>
                      </ul>
                    </div>

                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
                      <h4 className="font-semibold text-gray-900 mb-3">Affiliated Content & Cross-Promotion</h4>
                      <p className="text-sm text-gray-700">
                        StrymHub may feature content that promotes the founder's other businesses, platforms, 
                        and affiliated ventures. This cross-promotional content is part of our company ecosystem 
                        and helps us provide value across multiple platforms.
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5" />
                        <div className="text-amber-800">
                          <h4 className="font-semibold mb-1">No Guarantees</h4>
                          <p className="text-sm">
                            Creator partnerships are not guaranteed and remain at StrymHub's sole discretion. 
                            We are not accepting public applications for creator accounts. Any changes to 
                            these terms will be communicated to users via email and platform notifications.
                          </p>
                        </div>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600">
                      We're excited about the future of StrymHub and grateful for your support during this 
                      foundational phase. Your feedback and engagement help us build a platform that serves 
                      both creators and viewers excellently.
                    </p>
                  </div>
                </div>
              </section>

              {/* Last Updated */}
              <div className="bg-orange-50 p-6 rounded-lg text-center">
                <p className="text-orange-700 font-medium">
                  These Terms of Service were last updated on December 29, 2025
                </p>
                <p className="text-orange-600 text-sm mt-2">
                  Version 1.0.0 - Single Creator Model | Changes will be communicated via email and platform notifications
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default TermsOfServicePage;